import theme, { colors } from '@globals/themes/defaultTheme'
import { mobileQuery } from '@globals/themes/themeConstants'
import Autocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  AutocompleteRenderValueGetItemProps,
} from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Paper, { PaperProps } from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  AppliedFilters,
  LoadingMore,
  filterListSx,
  filterPopupSx,
} from '@shared/filters/appliedFilters/AppliedFilters'
import { useAppliedFiltersCollapse } from '@shared/filters/appliedFilters/useAppliedFiltersCollapse'
import {
  isNearListEnd,
  listNeedsAnotherPage,
  metricsOf,
  rowLayoutOf,
} from '@shared/filters/dynamicSelectUtils'
import { Selectable } from '@shared/filters/selections'
import { collapsedRowStyle, useRowWindow } from '@shared/filters/useRowWindow'
import FieldLabel from '@shared/forms/FieldLabel'
import { isValidSearchInput } from '@shared/utils/searchInputValidation'
import { T, useT } from '@transifex/react'
import {
  HTMLAttributes,
  Key,
  ReactElement,
  ReactNode,
  UIEvent,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDebounce } from 'use-debounce'

export const textFieldStyle: SxProps = {
  '& input': {
    '&::placeholder': {
      color: colors.grayMedium,
      opacity: 1,
    },
  },
}

export type AutoCompleteOption<T extends object | string = string> = {
  id: string
  value: T
  icon?: ReactElement
  avatar?: ReactElement
}

export type AutoCompleteResultsZIndexPlacement = 'modal' | 'page' | 'drawer'

export type AutoCompleteRenderChipProps = {
  key?: number | string
} & ReturnType<AutocompleteRenderValueGetItemProps<false>>

type StandardAutoCompleteSearchProps<T extends object | string = string> = {
  value: AutoCompleteOption<T>[] | AutoCompleteOption<T>
  onSearchTextChanged: (searchText: string) => void
  onSelectedOptionsChanged: (option: AutoCompleteOption<T>[]) => void
  label?: ReactNode
  placeholder: string
  searchText: string
  options: AutoCompleteOption<T>[]
  renderChip: (
    option: AutoCompleteOption<T>,
    props: AutoCompleteRenderChipProps,
    index?: number,
  ) => ReactNode
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: AutoCompleteOption<T>,
  ) => ReactNode
  groupBy?: (option: AutoCompleteOption<T>) => string
  id?: string
  multiple?: boolean
  disableClose?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
  debounceInput?: boolean
  loading?: boolean
  hideDropdown?: boolean
  readOnly?: boolean
  width?: number
  /**
   * Same popup as the filter `DynamicSelect`: first page on open, lazy-load on
   * scroll, selected items pinned above the list. Chips in the field stay.
   */
  paginated?: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onFetchNextPage?: () => void
  onOpenChange?: (open: boolean) => void
  getAppliedFilterLabel?: (option: AutoCompleteOption<T>) => string
}

const EMPTY_OPTION_ID = '__empty__'

const groupHeaderStyle: SxProps = {
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  height: 30,
  color: colors.grayPrimary,
  background: colors.grayUltraLight,
}

const placementZIndex: Record<AutoCompleteResultsZIndexPlacement, number> = {
  page: 1,
  modal: theme.zIndex.modal + 1,
  drawer: theme.zIndex.drawer + 1,
}

const AUTO_COMPLETE_DEBOUNCE_TIME = 250

const appliedFilterLabelOf = <T extends object | string>(option: AutoCompleteOption<T>): string => {
  const { value } = option
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && 'name' in value && typeof value.name === 'string') {
    return value.name
  }
  return option.id
}

/** The caller's grouping, with the empty-state placeholder kept out of its way. */
const groupKeyOf =
  <T extends object | string>(groupBy: (option: AutoCompleteOption<T>) => string) =>
  (option: AutoCompleteOption<T>) =>
    option.id === EMPTY_OPTION_ID ? EMPTY_OPTION_ID : groupBy(option)

/**
 * Gathers the options of each group together, in the order the groups first
 * appear and keeping each group's own order. Autocomplete starts a new group
 * every time consecutive options report a different key, so a list that comes
 * back to a key it has already used renders that header a second time — which is
 * what appending a selection the loaded pages did not return does to a list
 * paged as teams and then users.
 */
const orderedByGroup = <T extends object | string>(
  options: AutoCompleteOption<T>[],
  groupOf: (option: AutoCompleteOption<T>) => string,
): AutoCompleteOption<T>[] => {
  const grouped = new Map<string, AutoCompleteOption<T>[]>()
  options.forEach(option => {
    const group = groupOf(option)
    const members = grouped.get(group)
    if (members) {
      members.push(option)
    } else {
      grouped.set(group, [option])
    }
  })
  return [...grouped.values()].flat()
}

const StandardAutoCompleteSearch = <T extends object | string = string>({
  onSearchTextChanged,
  onSelectedOptionsChanged,
  label,
  placeholder,
  searchText,
  options,
  renderChip,
  renderOption,
  groupBy,
  id = 'autocompleteSearch',
  multiple = true,
  disabled = false,
  value,
  disableClose,
  zIndexPlacement = 'page',
  debounceInput = true,
  loading,
  hideDropdown = false,
  readOnly = false,
  width,
  paginated = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFetchNextPage,
  onOpenChange,
  getAppliedFilterLabel,
}: StandardAutoCompleteSearchProps<T>) => {
  const t = useT()
  const isMobile = useMediaQuery(mobileQuery)
  const [inputValue, setInputValue] = useState<string>('')
  const [deferredInputValue] = useDebounce(
    inputValue,
    debounceInput ? AUTO_COMPLETE_DEBOUNCE_TIME : 0,
  )
  const {
    hidden: appliedFiltersHidden,
    noteScroll,
    reset: resetAppliedFilters,
  } = useAppliedFiltersCollapse()

  useEffect(() => {
    if (isValidSearchInput(deferredInputValue)) {
      onSearchTextChanged(deferredInputValue)
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredInputValue])

  useEffect(() => {
    setInputValue(searchText)
  }, [searchText])

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!paginated) {
      setIsOpen(deferredInputValue.trim() !== '')
    }
  }, [deferredInputValue, paginated])

  const selectedOptions = useMemo(() => {
    if (Array.isArray(value)) return value
    return value ? [value] : []
  }, [value])

  const selectedIds = useMemo(
    () => new Set(selectedOptions.map(option => option.id)),
    [selectedOptions],
  )

  // Selected matches stay in `options` so Autocomplete can still match them to
  // the value, but they render as applied filters rather than rows. Selections
  // the search did not return are appended rather than slotted in, since
  // reordering the matches would read as a new option list and pull the listbox
  // back to the top — a grouped list then puts each group back together.
  const listOptions = useMemo(() => {
    if (!paginated) return options

    const optionIds = new Set(options.map(option => option.id))
    const unmatchedSelections = selectedOptions.filter(option => !optionIds.has(option.id))
    const hasVisibleOption = options.some(option => !selectedIds.has(option.id))
    const emptyOption: AutoCompleteOption<T> = {
      id: EMPTY_OPTION_ID,
      // Keep this an object so callers that do `'teamType' in option.value` do
      // not throw — `in` is illegal on a string, which is what we used to pass.
      value: (options[0]?.value ?? selectedOptions[0]?.value ?? {}) as T,
    }
    const combined = [
      ...options,
      ...unmatchedSelections,
      ...(hasVisibleOption || hasNextPage || isFetchingNextPage || loading ? [] : [emptyOption]),
    ]
    return groupBy ? orderedByGroup(combined, groupKeyOf(groupBy)) : combined
  }, [
    paginated,
    options,
    selectedOptions,
    selectedIds,
    hasNextPage,
    isFetchingNextPage,
    loading,
    groupBy,
  ])

  // A selection renders as an applied filter rather than a row, so a group left
  // holding nothing else — a selected team none of the loaded pages returned —
  // has no rows to sit under its header.
  const groupsWithRows = useMemo(() => {
    if (!paginated || !groupBy) return null
    const rows = listOptions.filter(
      option => option.id !== EMPTY_OPTION_ID && !selectedIds.has(option.id),
    )
    return new Set(rows.map(groupBy))
  }, [paginated, groupBy, listOptions, selectedIds])

  // Selected options render as applied filters rather than as rows, so the row an
  // option occupies is not its index in `listOptions`.
  const { rowOfOption, rowCount } = useMemo(
    () => rowLayoutOf(listOptions, option => selectedIds.has(option.id)),
    [listOptions, selectedIds],
  )

  // Which rows of a long list are worth rendering. Short lists render in full,
  // so this costs nothing until enough pages have been scrolled in to matter.
  const rowWindow = useRowWindow(rowCount)
  const { reset: resetRowWindow } = rowWindow

  const appliedFilterItems: Selectable[] = useMemo(
    () =>
      selectedOptions.map(option => ({
        id: option.id,
        name: (getAppliedFilterLabel ?? appliedFilterLabelOf)(option),
      })),
    [selectedOptions, getAppliedFilterLabel],
  )

  const appliedFilters: AppliedFiltersContextValue | null = useMemo(() => {
    if (!paginated || !multiple) return null
    return {
      items: appliedFilterItems,
      hidden: appliedFiltersHidden,
      isLoadingMore: isFetchingNextPage,
      onClear: () => onSelectedOptionsChanged([]),
      onDeselect: (item: Selectable) =>
        onSelectedOptionsChanged(selectedOptions.filter(option => option.id !== item.id)),
    }
  }, [
    paginated,
    multiple,
    appliedFilterItems,
    appliedFiltersHidden,
    isFetchingNextPage,
    onSelectedOptionsChanged,
    selectedOptions,
  ])

  useEffect(() => {
    if (!paginated) return
    resetAppliedFilters()
    resetRowWindow()
  }, [deferredInputValue, isOpen, paginated, resetAppliedFilters, resetRowWindow])

  const handleListboxScroll = (event: UIEvent<HTMLElement>) => {
    const metrics = metricsOf(event.currentTarget)
    rowWindow.retarget(event.currentTarget)
    noteScroll(metrics)
    if (isNearListEnd(metrics) && hasNextPage && !isFetchingNextPage) {
      onFetchNextPage?.()
    }
  }

  // Selected options render as null and Autocomplete skips those, but the rows
  // the window has passed over keep a stand-in the arrow keys can land on.
  // Moving the window onto that row swaps the stand-in for a real option.
  const handleHighlightChange = (_event: unknown, option: AutoCompleteOption<T> | null) => {
    if (!paginated || !option || option.id === EMPTY_OPTION_ID) return
    rowWindow.reveal(
      rowOfOption[listOptions.findIndex(candidate => candidate.id === option.id)] ?? -1,
    )
  }

  useEffect(() => {
    if (!paginated || !isOpen) return
    if (options.length === 0) return
    if (loading && !isFetchingNextPage) return
    if (hasNextPage && !isFetchingNextPage && listNeedsAnotherPage(rowCount)) {
      onFetchNextPage?.()
    }
  }, [
    paginated,
    isOpen,
    options.length,
    rowCount,
    loading,
    hasNextPage,
    isFetchingNextPage,
    onFetchNextPage,
  ])

  const setOpen = (open: boolean) => {
    setIsOpen(open)
    onOpenChange?.(open)
  }

  const handleOpen = () => {
    if (paginated) {
      setOpen(true)
      return
    }
    setIsOpen(deferredInputValue.trim() !== '' || options.length > 0)
  }

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    // The empty placeholder is only there so Autocomplete will show "No results"
    // while selected values stay in `options`. It is not a real group.
    if (params.group === EMPTY_OPTION_ID) {
      return <Box key={params.key}>{params.children}</Box>
    }
    if (groupsWithRows && !groupsWithRows.has(params.group)) {
      return null
    }
    return (
      <Stack key={params.key}>
        <Typography variant="caption" sx={groupHeaderStyle}>
          {params.group}
        </Typography>
        <Box>{params.children}</Box>
      </Stack>
    )
  }

  const renderListOption = (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: AutoCompleteOption<T>,
    state: AutocompleteRenderOptionState,
  ) => {
    if (option.id === EMPTY_OPTION_ID) {
      return (
        <Box key={option.id} sx={{ padding: '14px 16px' }}>
          <Typography>
            <T _str="No results found" />
          </Typography>
        </Box>
      )
    }
    if (paginated && state.selected) {
      return null
    }
    if (!paginated) {
      return renderOption?.(props, option)
    }

    const row = rowOfOption[state.index] ?? -1
    const { key, ...rowProps } = props

    // A stand-in keeps the option in the DOM so Autocomplete can land keyboard
    // highlight on it. Rendering nothing instead makes MUI skip the row, and the
    // rest of a long list can never be reached from the keyboard.
    if (!rowWindow.isRendered(row)) {
      return <li key={key} {...rowProps} role="presentation" style={collapsedRowStyle} />
    }

    return renderOption?.({ ...props, style: rowWindow.spacingOf(row) }, option)
  }

  const autocomplete = (
    <Autocomplete
      open={isOpen}
      onOpen={handleOpen}
      onClose={() => (paginated ? setOpen(false) : setIsOpen(false))}
      sx={textFieldStyle}
      multiple={multiple}
      disableCloseOnSelect={paginated ? (disableClose ?? true) : disableClose}
      id={id}
      filterOptions={option => option}
      value={value}
      options={listOptions}
      autoComplete
      fullWidth
      groupBy={groupBy ? groupKeyOf(groupBy) : undefined}
      renderGroup={groupBy ? renderGroup : undefined}
      getOptionLabel={option => {
        if (option.id === EMPTY_OPTION_ID) return ''
        if (paginated) return (getAppliedFilterLabel ?? appliedFilterLabelOf)(option)
        return (!renderOption && (option as AutoCompleteOption<string>).value) || ''
      }}
      // Autocomplete falls back to the label for each row's React key. Two teams
      // can share a name, and the duplicate key leaves React unable to tell the
      // rows apart — they lose their click and hover handlers and also results in additional duplicates.
      getOptionKey={option => option.id}
      getOptionDisabled={option =>
        option.id === EMPTY_OPTION_ID || (paginated && selectedIds.has(option.id))
      }
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={inputValue}
      onInputChange={(_, newText, reason) => {
        if (reason !== 'reset') {
          setInputValue(newText)
        }
      }}
      loading={loading && !isFetchingNextPage}
      loadingText={t('Loading results...')}
      noOptionsText={t('No results found')}
      onChange={(_, newOptions) => {
        if (multiple) {
          onSelectedOptionsChanged(
            (newOptions as AutoCompleteOption<T>[]).filter(option => option.id !== EMPTY_OPTION_ID),
          )
        } else {
          onSelectedOptionsChanged(newOptions !== null ? [newOptions as AutoCompleteOption<T>] : [])
        }
      }}
      onKeyDown={e => {
        if (!multiple && searchText === '' && value && e.key === 'Backspace') {
          onSelectedOptionsChanged([])
        }
      }}
      onHighlightChange={handleHighlightChange}
      renderValue={(selectedValue, getItemProps) => {
        if (multiple) {
          return (selectedValue as AutoCompleteOption<T>[]).map((selected, index) =>
            renderChip(selected, getItemProps({ index }), index),
          )
        } else {
          return renderChip(selectedValue as AutoCompleteOption<T>, getItemProps({ index: 0 }))
        }
      }}
      renderOption={renderListOption}
      renderInput={params => {
        const { slotProps, ...otherParams } = params
        return (
          <TextField
            {...otherParams}
            fullWidth
            placeholder={!disabled ? placeholder : ''}
            size="small"
            slotProps={{
              ...slotProps,
              input: {
                ...slotProps.input,
              },
              htmlInput: {
                ...slotProps.htmlInput,
                readOnly,
              },
            }}
          />
        )
      }}
      disabled={disabled}
      forcePopupIcon={hideDropdown ? false : undefined}
      slots={paginated ? { paper: PagedAutoCompletePaper } : undefined}
      slotProps={{
        listbox: paginated
          ? { sx: filterListSx, onScroll: handleListboxScroll }
          : {
              sx: {
                maxHeight: isMobile ? '23vh' : '40vh',
              },
            },
        popper: {
          sx: {
            zIndex: placementZIndex[zIndexPlacement],
          },
        },
      }}
    />
  )

  return (
    <Stack
      direction="column"
      useFlexGap
      sx={{
        gap: 0.25,
        width: { xs: '100%', sm: width },
      }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {paginated ? (
        <AppliedFiltersContext.Provider value={appliedFilters}>
          {autocomplete}
        </AppliedFiltersContext.Provider>
      ) : (
        autocomplete
      )}
    </Stack>
  )
}

type AppliedFiltersContextValue = {
  items: Selectable[]
  hidden: boolean
  isLoadingMore: boolean
  onClear: () => void
  onDeselect: (item: Selectable) => void
}

const AppliedFiltersContext = createContext<AppliedFiltersContextValue | null>(null)

type PaperSlotProps = PaperProps & {
  ownerState?: unknown
}

const PagedAutoCompletePaper = ({
  children,
  ownerState: _ownerState,
  ...paperProps
}: PaperSlotProps) => {
  const appliedFilters = useContext(AppliedFiltersContext)

  return (
    <Paper {...paperProps} sx={filterPopupSx}>
      {appliedFilters && (
        <AppliedFilters
          items={appliedFilters.items}
          hidden={appliedFilters.hidden}
          onClear={appliedFilters.onClear}
          onDeselect={appliedFilters.onDeselect}
          clearId="btnClearSelected"
        />
      )}
      {children}
      {appliedFilters?.isLoadingMore && <LoadingMore />}
    </Paper>
  )
}

export default StandardAutoCompleteSearch
